import { AGENT_TYPE, isCommunicationAgentType } from "./agentTypes";

/**
 * + menu chip visibility for one Agent type. Mirrors PRODUCT_IDEAS.md's
 * "What we're building" capability matrix.
 */
export interface AgentChipVisibility {
  socialPosts: boolean;
  blog: boolean;
  createImage: boolean;
  createVideo: boolean;
  redditFeed: boolean;
  /** Quick Search + Deep Research travel together — always yes/no as a pair. */
  quickSearchAndDeepResearch: boolean;
  /** Scan Business Card + Import Contacts travel together — always yes/no as a pair. */
  scanBusinessCardAndImportContacts: boolean;
  /** Still additionally requires a connected GHL account (see isGHLConnected). */
  outreachEmail: boolean;
}

const SOCIAL_MEDIA_MANAGER: AgentChipVisibility = {
  socialPosts: true,
  blog: true,
  createImage: true,
  createVideo: true,
  redditFeed: true,
  quickSearchAndDeepResearch: true,
  scanBusinessCardAndImportContacts: false,
  outreachEmail: false,
};

const RECEPTIONIST: AgentChipVisibility = {
  socialPosts: false,
  blog: false,
  createImage: false,
  createVideo: false,
  redditFeed: false,
  quickSearchAndDeepResearch: false,
  scanBusinessCardAndImportContacts: true,
  outreachEmail: true,
};

const VISIBILITY_MANAGER: AgentChipVisibility = {
  // Per the SGL-310 ticket's target matrix (reviewed/approved), Social Posts
  // and Blog stay directly on this agent even though PRODUCT_IDEAS.md's later
  // "chip lives on the executing employee" rule would route them through the
  // Social Media Manager instead — that hand-off is Multi-Agent Autopilot,
  // status "Idea — not started", so this agent keeps a direct path to both
  // until Autopilot actually ships.
  socialPosts: true,
  blog: true,
  createImage: true,
  createVideo: false,
  // SGL-411: opens the Brand's shared board (Content → Reddit, the Social Media Manager's) — never a board of its own.
  redditFeed: true,
  quickSearchAndDeepResearch: true,
  scanBusinessCardAndImportContacts: false,
  outreachEmail: false,
};

const GENERAL_ASSISTANT: AgentChipVisibility = {
  socialPosts: false,
  blog: false,
  createImage: true,
  createVideo: false,
  redditFeed: false,
  quickSearchAndDeepResearch: true,
  scanBusinessCardAndImportContacts: false,
  outreachEmail: false,
};

/**
 * Resolve + menu chip visibility from Agent type alone. Agent type is now the
 * only input — an agent's stored `tools` no longer decide which chips render
 * (that field still decides which output tool the backend is nudged toward
 * once a chip is chosen; see tool_registry.py::get_tools_for_agent on the
 * backend for the matching per-type tool set).
 *
 * content_creator (Social Media Manager) is also the fallback for any
 * unrecognized type, matching this menu's pre-existing default behavior.
 */
export function resolveAgentChipVisibility(
  agentType: string | undefined | null
): AgentChipVisibility {
  if (isCommunicationAgentType(agentType)) return RECEPTIONIST;
  if (agentType === AGENT_TYPE.VISIBILITY_MANAGER) return VISIBILITY_MANAGER;
  if (agentType === AGENT_TYPE.GENERAL) return GENERAL_ASSISTANT;
  return SOCIAL_MEDIA_MANAGER;
}

/**
 * The storable `tools` ids (AVAILABLE_TOOLS in AIAgents/constants/agentTools.ts)
 * this Agent type gets — same map as resolveAgentChipVisibility, so the Create/
 * Edit Agent tool checklist offers (and pre-ticks) exactly what the + menu
 * shows, instead of drifting from it. Only covers the tools that are actually
 * user-toggleable today (Social Posts, Blog, Generate Image) — Scan Business
 * Card, Import Contacts, Create Video, Reddit Feed, Quick Search/Deep Research
 * are never in AVAILABLE_TOOLS and stay purely type-driven via chipVisibility.
 */
export function toolsForAgentType(agentType: string | undefined | null): string[] {
  const v = resolveAgentChipVisibility(agentType);
  const tools: string[] = [];
  if (v.socialPosts) tools.push("output_social_posts");
  if (v.blog) tools.push("output_blog");
  if (v.createImage) tools.push("generate_image");
  return tools;
}
